import { $isCodeNode, CODE_LANGUAGE_MAP } from '@lexical/code';
import { $createLinkNode, $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isDecoratorBlockNode } from '@lexical/react/LexicalDecoratorBlockNode';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $getSelectionStyleValueForProperty, $patchStyleText } from '@lexical/selection';
import { $isTableNode, $isTableSelection } from '@lexical/table';
import {
  $findMatchingParent,
  $getNearestBlockElementAncestorOrThrow,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import {
  ActionIcon,
  Button,
  ColorPicker,
  Divider,
  Group,
  Menu,
  Popover,
  rem,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconBlockquote,
  IconBold,
  IconChevronDown,
  IconCircleOff,
  IconClearFormatting,
  IconCode,
  IconHighlight,
  IconItalic,
  IconLink,
  IconList,
  IconListCheck,
  IconListNumbers,
  IconMathFunction,
  IconPhoto,
  IconPlus,
  IconStrikethrough,
  IconSubscript,
  IconSuperscript,
  IconTable,
  IconTextColor,
  IconUnderline,
  IconUnlink,
  IconVideo
} from '@tabler/icons-react';
import {
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  KEY_ESCAPE_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';
import { InsertEquationDialog } from './EquationsPlugin';
import { InsertImageDialog } from './ImagesPlugin';
import { ParagraphStyleSelect } from './ParagraphStyleSelect';
import { InsertTableDialog } from './TablePlugin';
import {
  formatBulletList,
  formatCheckList,
  formatCode,
  formatNumberedList,
  formatQuote,
  getSelectedNode,
  sanitizeUrl,
} from './utils';
import { InsertVideoDialog } from './VideoPlugin';

const LowPriority = 1;

const DEFAULT_FONT_SIZE = 15;

function useToolbarState() {
  const [toolbarState, setToolbarState] = useState({
    bgColor: '#fff',
    blockType: 'paragraph',
    canRedo: false,
    canUndo: false,
    codeLanguage: '',
    elementFormat: 'left',
    fontColor: '#000',
    fontSize: `${DEFAULT_FONT_SIZE}px`,
    // Font size input value - for controlled input
    fontSizeInputValue: `${DEFAULT_FONT_SIZE}`,
    isBold: false,
    isImageCaption: false,
    isItalic: false,
    isLink: false,
    isRTL: false,
    isStrikethrough: false,
    isSubscript: false,
    isSuperscript: false,
    isUnderline: false,
    rootType: 'root',
  });

  const updateToolbarState = (key, value) => {
    setToolbarState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  return { toolbarState, updateToolbarState };
}

function ActionIconCycle({ editor, elementFormat }) {
  const iconsMapping = {
    left: IconAlignLeft,
    center: IconAlignCenter,
    right: IconAlignRight,
    justify: IconAlignJustified,
  };
  const options = Object.keys(iconsMapping);
  const [currentIndex, setCurrentIndex] = useState(0);
  const Component = iconsMapping[options[currentIndex]];

  useEffect(() => {
    setCurrentIndex(Math.max(options.indexOf(elementFormat), 0));
  }, [elementFormat]);

  function toggleNextIcon() {
    const nextAlignmentIndex = currentIndex + 1;
    const newIndex = nextAlignmentIndex <= options.length - 1 ? nextAlignmentIndex : 0;
    setCurrentIndex(newIndex);
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, options[newIndex]);
  }

  return (
    <ActionIcon
      size="lg"
      variant="subtle"
      onClick={toggleNextIcon}
      aria-label={`Format ${String(elementFormat).charAt(0).toUpperCase() + String(elementFormat).slice(1)}`}
    >
      <Component />
    </ActionIcon>
  );
}

export const clearFormatting = (editor) => {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const anchor = selection.anchor;
      const focus = selection.focus;
      const nodes = selection.getNodes();
      const extractedNodes = selection.extract();

      if (anchor.key === focus.key && anchor.offset === focus.offset) {
        return;
      }

      nodes.forEach((node, idx) => {
        // We split the first and last node by the selection
        // So that we don't format unselected text inside those nodes
        if ($isTextNode(node)) {
          // Use a separate variable to ensure TS does not lose the refinement
          let textNode = node;
          if (idx === 0 && anchor.offset !== 0) {
            textNode = textNode.splitText(anchor.offset)[1] || textNode;
          }
          if (idx === nodes.length - 1) {
            textNode = textNode.splitText(focus.offset)[0] || textNode;
          }
          /**
           * If the selected text has one format applied
           * selecting a portion of the text, could
           * clear the format to the wrong portion of the text.
           *
           * The cleared text is based on the length of the selected text.
           */
          // We need this in case the selected text only has one format
          const extractedTextNode = extractedNodes[0];
          if (nodes.length === 1 && $isTextNode(extractedTextNode)) {
            textNode = extractedTextNode;
          }

          if (textNode.__style !== '') {
            textNode.setStyle('');
          }
          if (textNode.__format !== 0) {
            textNode.setFormat(0);
            $getNearestBlockElementAncestorOrThrow(textNode).setFormat('');
          }
          node = textNode;
        } else if ($isHeadingNode(node) || $isQuoteNode(node)) {
          node.replace($createParagraphNode(), true);
        } else if ($isDecoratorBlockNode(node)) {
          node.setFormat('');
        }
      });
    }
  });
};

export default function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef(null);
  const [opened, setOpened] = useState(false);
  const [lastSelection, setLastSelection] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const { toolbarState, updateToolbarState } = useToolbarState();

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(linkUrl));
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const parent = getSelectedNode(selection).getParent();
            if ($isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(parent.getURL(), {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            }
          }
        });
      }
      setLinkUrl('');
      setOpened(false);
    }
  };

  const applyStyleText = useCallback(
    (styles: Record<string, string>, skipHistoryStack?: boolean) => {
      editor.update(
        () => {
          const selection = $getSelection();
          if (selection !== null) {
            $patchStyleText(selection, styles);
          }
        },
        skipHistoryStack ? { tag: 'historic' } : {}
      );
    },
    [editor]
  );

  const onFontColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ color: value }, false);
    },
    [applyStyleText]
  );

  const onBgColorSelect = useCallback(
    (value: string) => {
      applyStyleText({ 'background-color': value }, false);
    },
    [applyStyleText]
  );

  const $updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        setLinkUrl(linkParent.getURL());
        setOpened(true);
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
        setOpened(true);
      } else {
        setLinkUrl('');
      }
      if (opened) {
        setLinkUrl(linkUrl);
      }
    }
    const activeElement = document.activeElement;

    if (selection !== null) {
      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      setLastSelection(null);
      setOpened(false);
      setLinkUrl('');
    }

    return true;
  }, [editor, setOpened, opened, linkUrl]);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : $findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            });

      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      // Update links
      const node = getSelectedNode(selection);
      const parent = node.getParent();
      const isLink = $isLinkNode(parent) || $isLinkNode(node);
      updateToolbarState('isLink', isLink);

      const tableNode = $findMatchingParent(node, $isTableNode);
      if ($isTableNode(tableNode)) {
        updateToolbarState('rootType', 'table');
      } else {
        updateToolbarState('rootType', 'root');
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();

          updateToolbarState('blockType', type);
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type) {
            updateToolbarState('blockType', type);
          }
          if ($isCodeNode(element)) {
            const language = element.getLanguage() as keyof typeof CODE_LANGUAGE_MAP;
            updateToolbarState(
              'codeLanguage',
              language ? CODE_LANGUAGE_MAP[language] || language : ''
            );
            return;
          }
        }
      }
      updateToolbarState(
        'fontColor',
        $getSelectionStyleValueForProperty(selection, 'color', '#000')
      );
      updateToolbarState(
        'bgColor',
        $getSelectionStyleValueForProperty(selection, 'background-color', '#fff')
      );
      let matchingParent;
      if ($isLinkNode(parent)) {
        // If node is a link, we need to fetch the parent paragraph node to set format
        matchingParent = $findMatchingParent(
          node,
          (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
        );
      }

      // If matchingParent is a valid node, pass it's format type
      updateToolbarState(
        'elementFormat',
        $isElementNode(matchingParent)
          ? matchingParent.getFormatType()
          : $isElementNode(node)
            ? node.getFormatType()
            : parent?.getFormatType() || 'left'
      );
      updateToolbarState('isBold', selection.hasFormat('bold'));
      updateToolbarState('isItalic', selection.hasFormat('italic'));
      updateToolbarState('isUnderline', selection.hasFormat('underline'));
      updateToolbarState('isStrikethrough', selection.hasFormat('strikethrough'));
      updateToolbarState('isSuperscript', selection.hasFormat('superscript'));
      updateToolbarState('isSubscript', selection.hasFormat('subscript'));
    }
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          updateToolbarState('canUndo', payload);
          return false;
        },
        LowPriority
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          updateToolbarState('canRedo', payload);
          return false;
        },
        LowPriority
      ),
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateLinkEditor();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          $updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (toolbarState.isLink) {
            updateToolbarState('isLink', false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, $updateToolbar]);

  return (
    <Group gap={0} justify="left" ref={toolbarRef} mb="md">
      <ParagraphStyleSelect editor={editor} blockType={toolbarState.blockType} />
      <ActionIconCycle editor={editor} elementFormat={toolbarState.elementFormat} />
      <ActionIcon
        size="lg"
        variant={toolbarState.isBold ? 'light' : 'subtle'}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        aria-label="Format Bold"
      >
        <IconBold style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.isItalic ? 'light' : 'subtle'}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        aria-label="Format Italics"
      >
        <IconItalic style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.isUnderline ? 'light' : 'subtle'}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
        }}
        aria-label="Format Underline"
      >
        <IconUnderline style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.isStrikethrough ? 'light' : 'subtle'}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
        }}
        aria-label="Format Strikethrough"
      >
        <IconStrikethrough style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <Popover position="bottom-end" shadow="md">
        <Popover.Target>
          <ActionIcon size="lg" variant="subtle" aria-label="Change Font Color">
            <IconTextColor style={{ width: '70%', height: '70%' }} stroke={1.5} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Button
            leftSection={<IconCircleOff />}
            variant="outline"
            mb="sm"
            fullWidth
            onClick={() => onFontColorSelect(null)}
          >
            Default
          </Button>
          <ColorPicker
            format="hex"
            swatches={[
              '#2e2e2e',
              '#868e96',
              '#fa5252',
              '#e64980',
              '#be4bdb',
              '#7950f2',
              '#4c6ef5',
              '#228be6',
              '#15aabf',
              '#12b886',
              '#40c057',
              '#82c91e',
              '#fab005',
              '#fd7e14',
            ]}
            value={toolbarState.fontColor}
            onChange={onFontColorSelect}
          />
        </Popover.Dropdown>
      </Popover>
      <Popover position="bottom-end" shadow="md">
        <Popover.Target>
          <ActionIcon size="lg" variant="subtle" aria-label="Change Font Highlight">
            <IconHighlight style={{ width: '70%', height: '70%' }} stroke={1.5} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Button
            leftSection={<IconCircleOff />}
            variant="outline"
            mb="sm"
            fullWidth
            onClick={() => onBgColorSelect(null)}
          >
            Transparent
          </Button>
          <ColorPicker
            format="hex"
            swatches={[
              '#2e2e2e',
              '#868e96',
              '#fa5252',
              '#e64980',
              '#be4bdb',
              '#7950f2',
              '#4c6ef5',
              '#228be6',
              '#15aabf',
              '#12b886',
              '#40c057',
              '#82c91e',
              '#fab005',
              '#fd7e14',
            ]}
            value={toolbarState.bgColor}
            onChange={onBgColorSelect}
          />
        </Popover.Dropdown>
      </Popover>
      <ActionIcon
        size="lg"
        variant={toolbarState.blockType === 'code' ? 'light' : 'subtle'}
        onClick={() => formatCode(editor, toolbarState.blockType)}
        aria-label="Format Code"
      >
        <IconCode style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.blockType === 'quote' ? 'light' : 'subtle'}
        onClick={() => formatQuote(editor, toolbarState.blockType)}
        aria-label="Format Quote"
      >
        <IconBlockquote style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <Popover position="bottom-end" shadow="md" opened={opened} onChange={setOpened}>
        <Popover.Target>
          <ActionIcon
            size="lg"
            variant={toolbarState.isLink ? 'light' : 'subtle'}
            onClick={() => setOpened((o) => !o)}
            aria-label="Insert Link"
          >
            <IconLink style={{ width: '70%', height: '70%' }} stroke={1.5} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Group align="center" gap="xs">
            <TextInput
              value={linkUrl}
              className="link-input"
              w={300}
              style={{ flexGrow: 1 }}
              key="link"
              placeholder="https://example.com"
              onChange={(event) => setLinkUrl(event.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLinkSubmission();
                }
              }}
            />
            <Button
              size="sm"
              variant="filled"
              aria-label="Save"
              onClick={() => handleLinkSubmission()}
            >
              Save
            </Button>
            <ActionIcon
              size="lg"
              variant="filled"
              aria-label="Remove link"
              onClick={() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)}
            >
              <IconUnlink style={{ width: '70%', height: '70%' }} stroke={1.5} />
            </ActionIcon>
          </Group>
        </Popover.Dropdown>
      </Popover>
      <ActionIcon
        size="lg"
        variant="subtle"
        onClick={() => {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
          setOpened(false);
        }}
        aria-label="Remove Link"
      >
        <IconUnlink style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.isSuperscript ? 'light' : 'subtle'}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript');
        }}
        aria-label="Format Superscript"
      >
        <IconSuperscript style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.isSubscript ? 'light' : 'subtle'}
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript');
        }}
        aria-label="Format Subscript"
      >
        <IconSubscript style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <Divider orientation="vertical" />
      <ActionIcon
        size="lg"
        variant={toolbarState.blockType === 'bullet' ? 'light' : 'subtle'}
        onClick={() => formatBulletList(editor, toolbarState.blockType)}
        aria-label="Bulleted List"
      >
        <IconList style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.blockType === 'number' ? 'light' : 'subtle'}
        onClick={() => formatNumberedList(editor, toolbarState.blockType)}
        aria-label="Numbered List"
      >
        <IconListNumbers style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant={toolbarState.blockType === 'check' ? 'light' : 'subtle'}
        onClick={() => formatCheckList(editor, toolbarState.blockType)}
        aria-label="Checklist"
      >
        <IconListCheck style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <Divider orientation="vertical" />
      <ActionIcon
        size="lg"
        variant="subtle"
        onClick={() => clearFormatting(editor)}
        aria-label="Checklist"
      >
        <IconClearFormatting style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant="subtle"
        disabled={!toolbarState.canUndo}
        onClick={() => {
          editor.dispatchCommand(UNDO_COMMAND, undefined);
        }}
        aria-label="Undo"
      >
        <IconArrowBackUp style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <ActionIcon
        size="lg"
        variant="subtle"
        disabled={!toolbarState.canRedo}
        onClick={() => {
          editor.dispatchCommand(REDO_COMMAND, undefined);
        }}
        aria-label="Redo"
      >
        <IconArrowForwardUp style={{ width: '70%', height: '70%' }} stroke={1.5} />
      </ActionIcon>
      <Menu position="bottom-start" radius={0}>
        <Menu.Target>
          <Button
            variant="subtle"
            style={{ flexGrow: 1 }}
            leftSection={<IconPlus style={{ width: rem(16), height: rem(16) }} />}
            rightSection={<IconChevronDown style={{ width: rem(16), height: rem(16) }} />}
          >
            Insert
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Item
            onClick={() =>
              modals.open({
                title: 'Insert Image',
                children: <InsertImageDialog activeEditor={editor} modals={modals} />,
              })
            }
            leftSection={<IconPhoto style={{ width: rem(14), height: rem(14) }} />}
          >
            Image
          </Menu.Item>
          <Menu.Item
            leftSection={<IconTable style={{ width: rem(14), height: rem(14) }} />}
            onClick={() => {
              modals.open({
                title: 'Insert Table',
                children: <InsertTableDialog activeEditor={editor} modals={modals} />,
              });
            }}
          >
            Table
          </Menu.Item>
          <Menu.Item
            leftSection={<IconVideo style={{ width: rem(14), height: rem(14) }} />}
            onClick={() => {
              modals.open({
                title: 'Insert Video',
                children: <InsertVideoDialog activeEditor={editor} modals={modals} />,
              });
            }}
          >
            Video
          </Menu.Item>
          <Menu.Item
            leftSection={<IconMathFunction style={{ width: rem(14), height: rem(14) }} />}
            onClick={() => {
              modals.open({
                title: 'Insert Equation',
                children: <InsertEquationDialog activeEditor={editor} modals={modals} />,
              });
            }}
          >
            Equation
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
